import type { HTMLAttributes, ReactNode } from 'react';

import { Badge } from '@onyx/ui/components/colleague/badge';
import { Checkbox } from '@onyx/ui/components/colleague/checkbox';
import type { IconMapping } from '@onyx/ui/components/colleague/icon';
import { Icon } from '@onyx/ui/components/colleague/icon';
import { getClassName } from '@onyx/ui/style-utils';

import styles from './list-item-selectable.module.css';

type ListItemSelectableVariant = 'default' | 'checkbox' | 'radio' | 'toggle';

type Spacing = 'cosy' | 'compact' | 'comfortable' | 'capacious';

type ContainerHtmlAttributes = Pick<
  HTMLAttributes<HTMLDivElement>,
  | 'id'
  | 'role'
  | 'tabIndex'
  | 'aria-labelledby'
  | 'aria-describedby'
  | 'aria-label'
>;

export type ListItemSelectableProps = ContainerHtmlAttributes & {
  isDisabled?: boolean;
  isSelected?: boolean;
  badgeIcon?: IconMapping;
  onSelect?: () => void;
  label?: string;
  badgeLabel?: string;
  title?: string;
  detail?: string;
  variant?: ListItemSelectableVariant;
  spacing?: Spacing;
};

enum KeyboardActionKeys {
  Enter = 'Enter',
  Space = 'Space',
}

/* TODO: should be removed when Checkbox, Radio and Toggle components are ready */
const InputControl = ({
  variant,
  isSelected,
  onChange,
  id,
  label,
}: {
  variant: ListItemSelectableVariant;
  isSelected: boolean;
  onChange?: () => void;
  id?: string;
  label?: string;
}) => {
  if (variant === 'checkbox' || variant === 'toggle') {
    return (
      <Checkbox
        checked={isSelected}
        isLabelHidden
        onChange={onChange}
        label={label ?? ''}
        id={id ?? ''}
        readOnly
      />
    );
  }

  return (
    <input type={variant} checked={isSelected} onChange={onChange} readOnly />
  );
};

const LeadingComponent = ({
  variant,
  isSelected,
  onSelect,
  id,
  label,
}: {
  variant: ListItemSelectableVariant;
  isSelected: boolean;
  onSelect?: () => void;
  id?: string;
  label?: string;
}) => {
  if (variant === 'default' && isSelected) {
    return (
      <div className={styles.listItemIcon}>
        <Icon name="Done" size={24} />
      </div>
    );
  }

  /* TODO: should be replaced with Checkbox and/or Radio component when created */
  if (['checkbox', 'radio'].includes(variant)) {
    return (
      <InputControl
        variant={variant}
        isSelected={isSelected}
        onChange={onSelect}
        id={id}
        label={label}
      />
    );
  }

  return null;
};

const Container = ({
  children,
  isDisabled,
  isSelected,
  onSelect,
  variant,
  htmlAttributes,
  spacing,
}: {
  children: ReactNode;
  isDisabled: boolean;
  isSelected: boolean;
  variant?: ListItemSelectableVariant;
  onSelect?: () => void;
  htmlAttributes: ContainerHtmlAttributes;
  spacing: Spacing;
}) => {
  const {
    id,
    role,
    tabIndex,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledby,
    'aria-describedby': ariaDescribedby,
  } = htmlAttributes;

  const spacingMap: Record<Spacing, string> = {
    cosy: styles.cosy,
    comfortable: styles.comfortable,
    capacious: styles.capacious,
    compact: styles.compact,
  };

  const itemBasicStyles = getClassName([
    styles.listItem,
    spacingMap[spacing],
    isDisabled && styles.disabled,
    isSelected && variant === 'default'
      ? styles.selectedDefault
      : styles.primary,
  ]);

  if (onSelect) {
    return (
      <button
        id={id}
        disabled={isDisabled}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
        aria-describedby={ariaDescribedby}
        className={getClassName([itemBasicStyles, styles.clickable])}
        onClick={() => onSelect?.()}
        onKeyDown={
          !isDisabled
            ? (e) => {
                if (
                  e.key === KeyboardActionKeys.Enter ||
                  e.code === KeyboardActionKeys.Space
                ) {
                  e.preventDefault();
                  onSelect?.();
                }
              }
            : undefined
        }
      >
        {children}
      </button>
    );
  }

  return (
    <div
      id={id}
      role={role}
      tabIndex={tabIndex}
      className={itemBasicStyles}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledby}
      aria-describedby={ariaDescribedby}
    >
      {children}
    </div>
  );
};

export const ListItemSelectable = ({
  id,
  role,
  tabIndex,
  'aria-label': ariaLabelAttribute,
  'aria-labelledby': ariaLabelledby,
  'aria-describedby': ariaDescribedby,
  isDisabled = false,
  isSelected = false,
  onSelect,
  badgeIcon,
  badgeLabel = '',
  label,
  title,
  detail,
  variant = 'default',
  spacing = 'compact',
}: ListItemSelectableProps) => {
  const htmlAttributes: ContainerHtmlAttributes = {
    id,
    role,
    tabIndex,
    'aria-label': ariaLabelAttribute,
    'aria-labelledby': ariaLabelledby,
    'aria-describedby': ariaDescribedby,
  };
  const isBadgeVisible = !!badgeLabel && badgeIcon;
  return (
    <Container
      isDisabled={isDisabled}
      isSelected={isSelected}
      spacing={spacing}
      onSelect={onSelect}
      variant={variant}
      htmlAttributes={htmlAttributes}
    >
      <LeadingComponent
        variant={variant}
        isSelected={isSelected}
        onSelect={onSelect}
        id={id ?? ''}
        label={label ?? ''}
      />

      <div className={styles.content}>
        {!!label && <h3 className={styles.label}>{label}</h3>}
        {!!title && <p className={styles.title}>{title}</p>}
        {!!detail && (
          <span
            className={getClassName([styles.listItemDetail, styles.detail])}
          >
            {detail}
          </span>
        )}
      </div>

      {(isBadgeVisible || variant === 'toggle') && (
        <div className={styles.badge}>
          {variant === 'toggle' ? (
            /* TODO: should be replaced with Toggle component when created */
            <InputControl
              variant="toggle"
              isSelected={isSelected}
              onChange={onSelect}
            />
          ) : (
            <Badge
              icon={badgeIcon}
              size="large"
              label={badgeLabel}
              variant="primary"
              color="neutral"
            />
          )}
        </div>
      )}
    </Container>
  );
};
